import _fetch from 'isomorphic-fetch';

import generalHeaders from './fetch-headers';
import toResult from './result';

import type { DefaultErrorDetails, Result } from '@avalara/types/index';

async function servePdfBlob(response: Response) {
  const blob = await response.blob();
  const contentDisposition = response.headers.get('Content-Disposition');
  const executedRegex = /filename="(.*?)"/gi.exec(contentDisposition || '');
  if (executedRegex && typeof window !== 'undefined') {
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    [, link.download] = executedRegex;
    link.click();
  }
}

export type ClientFetchOptions = {
  expectBlob?: boolean;
  url: RequestInfo;
  params?: RequestInit;
  timeout?: number;
  onSchemaValidationErr?: (arg0?: unknown) => void;
};

async function clientFetch<S, E = DefaultErrorDetails>({
  expectBlob,
  url,
  params = {},
  timeout = 30000,
  onSchemaValidationErr = () => {},
}: ClientFetchOptions): Promise<Result<S, E>> {
  try {
    const paramsWithGeneralHeaders = params || {};
    if (paramsWithGeneralHeaders.headers == null) {
      paramsWithGeneralHeaders.headers = generalHeaders;
    } else {
      const headers = new Headers(params.headers);
      for (const key of Object.keys(generalHeaders)) headers.append(key, generalHeaders[key]);
      paramsWithGeneralHeaders.headers = headers;
    }
    const fetchResultPromise: Promise<Response> = _fetch(url, paramsWithGeneralHeaders);
    const timeoutPromise: Promise<null> = new Promise((resolve) => {
      setTimeout(resolve, timeout, null);
    });
    const response: Response | null = await Promise.race([fetchResultPromise, timeoutPromise]);

    if (!response) {
      return toResult<S, E>(null, 'Timeout', 408);
    }

    const { status } = response;

    // If our API call failed, return an Error Result
    if (!response.ok) {
      const json = await response.json();
      let errMessage = 'Unhandled server error';
      let errDetails: E | undefined;

      if (typeof json.error === 'string') {
        errMessage = json.error;
      } else if (json.error) {
        errDetails = json.error.details || [];
        errMessage = json.error.message;
      } else if (json.message) {
        errMessage = json.message;
      } else if (Array.isArray(json.messages)) {
        errMessage = JSON.stringify(json.messages);
      }

      if (json.errors) {
        onSchemaValidationErr(json.errors);
      }

      if (!errDetails) errDetails = json.details;

      return toResult<S, E>(null, errMessage, status, errDetails);
    }

    if (response.headers.get('content-type') === 'application/pdf') {
      await servePdfBlob(response);
      return toResult<S, E>(null, 'File received', 200);
    }

    if (expectBlob === true) {
      const blob: Blob = await response.blob();
      return { ok: true, value: blob } as Result<S, E>;
    }

    // NOTE: Some endpoints do not return a body with a success response
    // Attempting to parse that JSON will throw an error. This is a workaround to avoid throwing errors
    // on a successful call
    const data = await response.text();
    const json = data !== '' ? JSON.parse(data) : {};

    return { ok: true, value: json };
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Unknown error';
    return toResult<S, E>(null, errorMessage, 500);
  }
}

export default clientFetch;
